import {
  getDisplayableRoasBaseline,
  TOTAL_FALLBACK_STANDARD_MODE,
} from '@/lib/dr-utils'
import { formatMultiplier } from '@/lib/format-utils'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  Legend,
} from 'recharts'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useState } from 'react'
import * as React from 'react'
import { getWeek } from '@/lib/date-utils'
import { useRoasStore } from '@/stores/roas'
import { shortCampaignLabel } from '../utils'

// Define colors for different models
const MODEL_COLORS = [
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#14b8a6', // teal
  '#a855f7', // purple
]

const hasUsableTargetRoas = (roasLine, totalFallbackMap) => {
  if (!roasLine) return false
  if (typeof roasLine === 'number') return roasLine > 0

  const day0Baselines = roasLine[0]
  if (!day0Baselines || typeof day0Baselines !== 'object') return false

  const dates = Object.keys(day0Baselines)
  if (dates.length === 0) return false

  if (TOTAL_FALLBACK_STANDARD_MODE !== 'hide') return true

  return dates.some((date) => !totalFallbackMap?.[0]?.[date])
}

const RoasAnalysisChart = ({
  install_date,
  days,
  cumulativeRevenueDays, // 新增参数
  cost,
  dayCosts = {},
  allModelsData,
  allModelsDayCosts = new Map(),
  allModelsCumulativeData = new Map(), // 新增参数
  compareRoas,
  compareDays,
  compareCumulativeRevenueDays, // 新增参数
  compareCost,
  compareDayCosts = {},
  displayDate,
  allData = [],
  summaryMode,
  displayMode,
  dayMode,
  day0RoasLine, // 新增参数
  day0RoasLineTotalFallback,
  mainRowIndex, // 主表行索引（0-based），日期对比时用于按索引取对比时段数据
}) => {
  const { setDisplayMode, setDayMode, multiplierDenominatorMode, setMultiplierDenominatorMode } = useRoasStore()
  const isDaily = summaryMode === 'daily' || !summaryMode
  const showMultiplier = displayMode === 'multiplier'
  const isCumulative = dayMode === 'cumulative'

  // 检查是否有目标 ROAS 基准线
  const hasTargetRoas = React.useMemo(() => {
    return hasUsableTargetRoas(day0RoasLine, day0RoasLineTotalFallback)
  }, [day0RoasLine, day0RoasLineTotalFallback])
  
  const getDenominator = (row) => {
    if (multiplierDenominatorMode === 'growth_rate') return null
    
    if (multiplierDenominatorMode === 'target_roas') {
      const target = getDisplayableRoasBaseline(
        day0RoasLine,
        day0RoasLineTotalFallback,
        0,
        row.install_date,
        TOTAL_FALLBACK_STANDARD_MODE,
      )
      return row.cost * target
    }
    
    // d0_revenue - Use day0 from whichever days object is provided (days or cumulativeRevenueDays)
    const rDays = row.cumulativeRevenueDays || row.days || row.compareCumulativeRevenueDays || row.compareDays
    return rDays?.['day0'] || 0
  }


  // 冠军模式下自动使用 allModelsData 中的所有模型（通常只有一个）
  const chartSelectedModels = React.useMemo(() => {
    if (!allModelsData || !(allModelsData instanceof Map) || compareRoas || !isDaily) {
      return []
    }
    return [...allModelsData.keys()]
  }, [allModelsData, compareRoas, isDaily])

  // State for hiding Day 0 (useful for Multiplier + Daily mode)
  const [hideDay0, setHideDay0] = useState(false)

  const [selectedDates, setSelectedDates] = useState([])
  const [dateSearchTerm, setDateSearchTerm] = useState('')

  const filteredCompareDates = React.useMemo(() => {
    return allData
      .filter((d) => d.install_date !== install_date)
      .filter((d) => {
        if (!dateSearchTerm) return true
        const label = (d.displayDate || d.install_date).toLowerCase()
        return label.includes(dateSearchTerm.toLowerCase())
      })
      .sort((a, b) => b.install_date.localeCompare(a.install_date))
  }, [allData, install_date, dateSearchTerm])

  // Initialize hideDay0 state based on mode
  // Default to hiding Day 0 when in Multiplier Daily mode (as D0 is usually 100% or 1.0x and skews chart)
  React.useEffect(() => {
    if (showMultiplier && !isCumulative) {
        setHideDay0(true)
    } else {
        setHideDay0(false)
    }
  }, [showMultiplier, isCumulative])


  const handleDateToggle = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    )
  }

  // Transform data for chart（日期对比按 mainRowIndex 取对应行，非日期对比按 install_date 匹配）
  const resolvedCompareData = React.useMemo(() => {
    if (!compareRoas || !Array.isArray(compareRoas) || compareRoas.length === 0) return null
    return compareRoas.map((c) => {
      let row = null
      if (c.isDateCompare && typeof mainRowIndex === 'number' && c.dailyData?.length) {
        const sorted = [...c.dailyData].sort((a, b) =>
          (b.install_date || '').localeCompare(a.install_date || ''),
        )
        row = sorted[mainRowIndex] ?? null
      } else {
        row = c.dailyData?.find((item) => (item.date || item.install_date) === install_date) ?? null
      }
      if (!row) return null
      const shortName = shortCampaignLabel(c.campaignName || 'Compare')
      const compareLabel = c.isDateCompare && (row.install_date || row.displayDate)
        ? `${shortName} ${row.install_date || row.displayDate}`
        : shortName
      return {
        name: compareLabel,
        days: row.days,
        cumulativeRevenueDays: row.cumulativeRevenueDays,
        cost: row.cost,
        dayCosts: row.dayCosts || {},
      }
    }).filter(Boolean).filter((c) => c.days || c.cumulativeRevenueDays)
  }, [compareRoas, install_date, mainRowIndex])

  const { chartData, yAxisMax, actualLabel, predictCumulativeData, otherDatesDataMap } = React.useMemo(() => {
    if (!days) return { chartData: [], maxValue: 0, yAxisMax: 100, actualLabel: '', predictCumulativeData: new Map(), otherDatesDataMap: new Map() }

    const maxDayTruth =
      Object.keys(days).length > 0
        ? Math.max(
            ...Object.keys(days).map((key) => parseInt(key.replace('day', ''))),
          )
        : 0
  
    // Get compare data max day if exists
    let maxDayCompare = 0
    if (resolvedCompareData && resolvedCompareData.length > 0) {
      resolvedCompareData.forEach(c => {
        const daysKeys = Object.keys(c.days || {})
        const cumKeys = Object.keys(c.cumulativeRevenueDays || {})
        const allKeys = [...new Set([...daysKeys, ...cumKeys])]
        if (allKeys.length > 0) {
          const currentMax = Math.max(...allKeys.map(k => parseInt(k.replace('day', ''))))
          maxDayCompare = Math.max(maxDayCompare, currentMax)
        }
      })
    } else if (compareDays && Object.keys(compareDays).length > 0) {
      maxDayCompare = Math.max(
        ...Object.keys(compareDays).map((key) =>
          parseInt(key.replace('day', '')),
        ),
      )
    }
  
    // Collect all prediction data for selected models (disabled in compare mode or non-daily mode)
    const predictDataByModel = new Map()
    let maxDayPredict = maxDayTruth
  
    if (
      !compareRoas &&
      isDaily &&
      allModelsData &&
      allModelsData instanceof Map
    ) {
      chartSelectedModels.forEach((modelKey) => {
        const modelData = allModelsData.get(modelKey)
        if (modelData) {
          const maxDay =
            Object.keys(modelData).length > 0
              ? Math.max(
                  ...Object.keys(modelData).map((key) =>
                    parseInt(key.replace('day', '')),
                  ),
                )
              : 0
          maxDayPredict = Math.max(maxDayPredict, maxDay)
          predictDataByModel.set(modelKey, modelData)
        }
      })
    }
  
    const maxDayTotal = Math.max(maxDayTruth, maxDayCompare, maxDayPredict)

    const truthData = {}
    let cumulativeRevenue = 0
    
    // Process multiple compare sources
    const multipleCompareData = (resolvedCompareData || []).map(cSource => ({
      name: cSource.name,
      data: {},
      cumulativeRevenue: 0
    }))

    for (let i = 0; i <= maxDayTotal; i++) {
      const dayKey = `day${i}`
      let revenue = 0

      if (isCumulative) {
        if (cumulativeRevenueDays && cumulativeRevenueDays[dayKey] !== undefined) {
          revenue = cumulativeRevenueDays[dayKey]
        } else if (days && days[dayKey] !== undefined) {
          cumulativeRevenue += days[dayKey]
          revenue = cumulativeRevenue
        }
      } else {
        if (days && days[dayKey] !== undefined) {
          revenue = days[dayKey]
        }
      }

      const hasData = (cumulativeRevenueDays && cumulativeRevenueDays[dayKey] !== undefined) || (days && days[dayKey] !== undefined)
      
      if (hasData) {
        let value = 0
        if (displayMode === 'amount') {
          value = revenue
        } else {
          const applicableCost = dayCosts[dayKey] || cost
          const denominator = showMultiplier 
            ? getDenominator({ install_date, cost, days, cumulativeRevenueDays }) 
            : applicableCost
          
          if (showMultiplier && multiplierDenominatorMode === 'growth_rate') {
            if (i === 0) {
              value = 0
            } else {
              let prevRev = 0
              if (isCumulative) {
                prevRev = (cumulativeRevenueDays && cumulativeRevenueDays[`day${i - 1}`] !== undefined) 
                  ? cumulativeRevenueDays[`day${i - 1}`] 
                  : (cumulativeRevenue - (days[`day${i}`] || 0))
              } else {
                prevRev = days[`day${i - 1}`] || 0
              }
              if (prevRev) {
                value = showMultiplier ? (revenue / prevRev) : ((revenue / prevRev) * 100)
              } else {
                value = 0
              }
            }
          } else if (denominator) {
            value = showMultiplier ? (revenue / denominator) : ((revenue / denominator) * 100)
          }
        }
        if (displayMode === 'amount' || value !== 0 || i === 0) {
           truthData[i] = { value }
        }
      }

      // Process compare sources for this day
      multipleCompareData.forEach((cSource, idx) => {
        const sourceMeta = resolvedCompareData[idx]
        let cRevenue = 0
        let cHasData = false

        if (isCumulative) {
          if (sourceMeta.cumulativeRevenueDays && sourceMeta.cumulativeRevenueDays[dayKey] !== undefined) {
            cRevenue = sourceMeta.cumulativeRevenueDays[dayKey]
            cHasData = true
          } else if (sourceMeta.days && sourceMeta.days[dayKey] !== undefined) {
            cSource.cumulativeRevenue += sourceMeta.days[dayKey]
            cRevenue = cSource.cumulativeRevenue
            cHasData = true
          }
        } else {
          if (sourceMeta.days && sourceMeta.days[dayKey] !== undefined) {
            cRevenue = sourceMeta.days[dayKey]
            cHasData = true
          }
        }

        if (cHasData) {
          let cValue = 0
          if (displayMode === 'amount') {
            cValue = cRevenue
          } else {
            const applicableCost = sourceMeta.dayCosts[dayKey] || sourceMeta.cost
            const denominator = showMultiplier 
              ? getDenominator({ install_date, cost: sourceMeta.cost, days: sourceMeta.days, cumulativeRevenueDays: sourceMeta.cumulativeRevenueDays }) 
              : applicableCost
            
            if (showMultiplier && multiplierDenominatorMode === 'growth_rate') {
              if (i === 0) {
                cValue = 0
              } else {
                let prevRev = 0
                if (isCumulative) {
                  prevRev = (sourceMeta.cumulativeRevenueDays && sourceMeta.cumulativeRevenueDays[`day${i - 1}`] !== undefined) 
                    ? sourceMeta.cumulativeRevenueDays[`day${i - 1}`] 
                    : (cSource.cumulativeRevenue - (sourceMeta.days[dayKey] || 0))
                } else {
                  prevRev = sourceMeta.days[`day${i - 1}`] || 0
                }
                if (prevRev) {
                  cValue = showMultiplier ? (cRevenue / prevRev) : ((cRevenue / prevRev) * 100)
                } else {
                  cValue = 0
                }
              }
            } else if (denominator) {
              cValue = showMultiplier ? (cRevenue / denominator) : ((cRevenue / denominator) * 100)
            }
          }
          cSource.data[i] = { value: cValue }
        }
      })
    }

    const otherDatesDataMap = new Map()
    selectedDates.forEach((date) => {
      const row = allData.find((d) => d.install_date === date)
      if (row && (row.days || row.cumulativeRevenueDays) && row.cost) {
        const dateData = { values: {}, displayLabel: row.displayDate || date }
        let cumRev = 0
        for (let i = 0; i <= maxDayTotal; i++) {
          const dayKey = `day${i}`
          let revenue = 0
          let hasData = false
          if (isCumulative) {
            if (row.cumulativeRevenueDays && row.cumulativeRevenueDays[dayKey] !== undefined) {
              revenue = row.cumulativeRevenueDays[dayKey]
              hasData = true
            } else if (row.days && row.days[dayKey] !== undefined) {
              cumRev += row.days[dayKey]
              revenue = cumRev
              hasData = true
            }
          } else {
            if (row.days && row.days[dayKey] !== undefined) {
              revenue = row.days[dayKey]
              hasData = true
            }
          }
          if (hasData) {
            if (displayMode === 'amount') {
               dateData.values[i] = revenue
            } else {
               const applicableCost = row.dayCosts?.[dayKey] || row.cost
               const denominator = showMultiplier ? getDenominator(row) : applicableCost
               if (showMultiplier && multiplierDenominatorMode === 'growth_rate') {
                 if (i === 0) dateData.values[i] = 0
                 else {
                   let prevRev = isCumulative 
                     ? (row.cumulativeRevenueDays?.[`day${i - 1}`] || (cumRev - (row.days[`day${i}`] || 0)))
                     : (row.days[`day${i - 1}`] || 0)
                   if (prevRev) dateData.values[i] = showMultiplier ? (revenue / prevRev) : ((revenue / prevRev) * 100)
                   else dateData.values[i] = 0
                 }
               } else if (denominator) {
                  dateData.values[i] = showMultiplier ? (revenue / denominator) : ((revenue / denominator) * 100)
               }
            }
          }
        }
        otherDatesDataMap.set(date, dateData)
      }
    })

    const predictCumulativeData = new Map()
    if (!compareRoas && isDaily) {
      predictDataByModel.forEach((modelData, modelKey) => {
        const cumulativeData = {}
        let cumRevenue = 0
        const modelCosts = allModelsDayCosts.get?.(modelKey) || {}
        const modelCumulativeRevenue = allModelsCumulativeData.get?.(modelKey)
        for (let i = 0; i <= maxDayTotal + 30; i++) {
          const dayKey = `day${i}`
          let revenue = 0
          let hasData = false
          if (isCumulative) {
            if (modelCumulativeRevenue && modelCumulativeRevenue[dayKey] !== undefined) {
              revenue = modelCumulativeRevenue[dayKey]
              hasData = true
            } else if (modelData[dayKey] !== undefined) {
              cumRevenue += modelData[dayKey]
              revenue = cumRevenue
              hasData = true
            }
          } else {
            if (modelData[dayKey] !== undefined) {
              revenue = modelData[dayKey]
              hasData = true
            }
          }
          if (hasData) {
            if (displayMode === 'amount') {
               cumulativeData[i] = revenue
            } else {
               const applicableCost = modelCosts[dayKey] || cost
               const denominator = showMultiplier ? getDenominator({ install_date, cost, days, cumulativeRevenueDays }) : applicableCost
               if (showMultiplier && multiplierDenominatorMode === 'growth_rate') {
                 if (i === 0) cumulativeData[i] = 0
                 else {
                   let prevRev = isCumulative 
                     ? (modelCumulativeRevenue?.[`day${i - 1}`] || (cumRevenue - (modelData[dayKey] || 0)))
                     : (modelData[`day${i - 1}`] || 0)
                   if (prevRev) cumulativeData[i] = showMultiplier ? (revenue / prevRev) : ((revenue / prevRev) * 100)
                   else cumulativeData[i] = 0
                 }
               } else if (denominator) {
                  cumulativeData[i] = showMultiplier ? (revenue / denominator) : ((revenue / denominator) * 100)
               }
            }
          }
        }
        predictCumulativeData.set(modelKey, cumulativeData)
      })
    }

    const chartDataResult = []
    const actualLabelStr = displayDate || install_date
    let max = 0
    for (let i = 0; i <= maxDayTotal + 30; i++) {
      if (i === 0 && hideDay0) continue
      const point = { day: `Day${i}` }
      chartDataResult.push(point)
      if (truthData[i]) {
        point[actualLabelStr] = truthData[i].value
        max = Math.max(max, truthData[i].value)
      }
      multipleCompareData.forEach((cSource) => {
        if (cSource.data[i]) {
          point[cSource.name] = cSource.data[i].value
          max = Math.max(max, cSource.data[i].value)
        }
      })
      otherDatesDataMap.forEach((dateData, date) => {
        if (dateData.values[i] !== undefined) {
          point[date] = dateData.values[i]
          max = Math.max(max, dateData.values[i])
        }
      })
      if (!compareRoas && isDaily) {
        predictCumulativeData.forEach((cumulativeData, modelKey) => {
          if (cumulativeData[i] !== undefined) {
            point[modelKey] = cumulativeData[i]
            max = Math.max(max, cumulativeData[i])
          }
        })
      }
    }

    let yMax = 100
    if (displayMode === 'amount') {
      yMax = Math.ceil(max * 1.1) // 10% padding
    } else {
      yMax = showMultiplier ? (Math.ceil(max * 1.1 * 10) / 10 || 1) : (Math.ceil(max / 50) * 50 || 100)
    }

    return { chartData: chartDataResult, maxValue: max, yAxisMax: yMax, actualLabel: actualLabelStr, predictCumulativeData, otherDatesDataMap }
  }, [
    days, isCumulative, cumulativeRevenueDays, displayMode, dayCosts, cost, showMultiplier, multiplierDenominatorMode,
    install_date, day0RoasLine, compareRoas, resolvedCompareData, compareDays, compareCost, compareCumulativeRevenueDays, compareDayCosts,
    selectedDates, allData, isDaily, chartSelectedModels, allModelsData, allModelsDayCosts, allModelsCumulativeData, hideDay0, displayDate
  ])

  if (!days) return null


  return (

    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold">
          {displayDate ||
            (() => {
              const dateStr = install_date.replace(/-/g, '')
              const [, weekDay] = getWeek(dateStr)
              return `${install_date} 周${weekDay}`
            })()}
          {resolvedCompareData && resolvedCompareData.length > 0
            ? ` (Compare: ${resolvedCompareData.map((c) => c.name).join(', ')})`
            : compareRoas && (Array.isArray(compareRoas) ? compareRoas.length > 0 : true) &&
              ` (Compare: ${(Array.isArray(compareRoas) ? compareRoas : [compareRoas]).map((c) => shortCampaignLabel(c.campaignName || 'Compare')).join(', ')})`}
        </h2>
        <div className="flex flex-wrap gap-2 items-center bg-muted/30 p-1 rounded-md">
          {/* Toggles */}
          <div className="flex items-center gap-2 mr-2">
            <Tabs value={displayMode} onValueChange={setDisplayMode}>
              <TabsList className="h-8">
                <TabsTrigger value="amount" className="text-xs px-2">$</TabsTrigger>
                <TabsTrigger value="percentage" className="text-xs px-2">%</TabsTrigger>
                <TabsTrigger value="multiplier" className="text-xs px-2">x</TabsTrigger>
              </TabsList>
            </Tabs>

            {showMultiplier && (
              <Select
                value={multiplierDenominatorMode}
                onValueChange={setMultiplierDenominatorMode}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="d0_revenue">D0 Revenue</SelectItem>
                  <SelectItem value="target_roas" disabled={!hasTargetRoas}>
                    Target ROAS {!hasTargetRoas && '(No Target ROAS Set)'}
                  </SelectItem>
                  <SelectItem value="growth_rate">Growth Rate</SelectItem>
                </SelectContent>
              </Select>
            )}

            <Tabs value={dayMode} onValueChange={setDayMode}>
              <TabsList className="h-8">
                <TabsTrigger value="daily" className="text-xs px-2">Daily</TabsTrigger>
                <TabsTrigger value="cumulative" className="text-xs px-2">Cumulative</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Hide D0 Checkbox - Only for Multiplier mode */}
          {showMultiplier && (
            <div className="flex items-center space-x-2 mr-2">
              <Checkbox
                id="hide-day0"
                checked={hideDay0}
                onCheckedChange={setHideDay0}
              />
              <label
                htmlFor="hide-day0"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Hide D0
              </label>
            </div>
          )}

          {/* Dates selector */}
          {allData && allData.length > 1 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[220px] justify-between border-primary/20 hover:border-primary"
                >
                  <span className="truncate font-semibold">
                    {selectedDates.length === 0
                      ? 'Compare other dates'
                      : `Comparing ${selectedDates.length} date(s)`}
                  </span>
                  <span className="ml-2">▼</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-2">
                <div className="pb-2 border-b mb-2">
                  <Input
                    placeholder="Search date..."
                    value={dateSearchTerm}
                    onChange={(e) => setDateSearchTerm(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div 
                  className="h-60 overflow-y-auto pr-2 custom-scrollbar"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <div className="space-y-2">
                    {filteredCompareDates.length === 0 ? (
                      <div className="text-center py-4 text-xs text-muted-foreground">
                        No dates found
                      </div>
                    ) : (
                      filteredCompareDates.map((d) => (
                        <div
                          key={d.install_date}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`date-${d.install_date}`}
                            checked={selectedDates.includes(d.install_date)}
                            onCheckedChange={() =>
                              handleDateToggle(d.install_date)
                            }
                          />
                          <label
                            htmlFor={`date-${d.install_date}`}
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            {d.displayDate || d.install_date}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

        </div>
      </div>
      <ResponsiveContainer width="100%" height={500}>
        <LineChart data={chartData}>
          <XAxis
            dataKey="day"
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            domain={[0, yAxisMax]}
            tickFormatter={(value) => {
                if (displayMode === 'amount') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(value)
                return showMultiplier ? formatMultiplier(value, 0, 1) : `${value.toFixed(0)}%`
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="grid gap-2">
                      <div className="font-semibold">
                        {payload[0].payload.day}
                      </div>
                      {payload.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-sm">
                            {entry.name}: {
                                displayMode === 'amount'
                                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(entry.value)
                                : showMultiplier ? formatMultiplier(entry.value) : `${entry.value?.toFixed(2)}%`
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              }
              return null
            }}
          />
          <Legend />
          {/* Reference lines for specific days */}
          {[3, 7, 14, 30, 45, 60, 90, 120, 150].map((day) => (
            <ReferenceLine
              key={`day-${day}`}
              x={`Day${day}`}
              stroke="#888888"
              strokeDasharray="3 3"
              label={{
                value: `Day${day}`,
                position: 'right',
                fontSize: 12,
                fill: '#888888',
              }}
            />
          ))}
          {/* Reference lines for percentages (only show in non-amount modes) */}
          {displayMode !== 'amount' && (
            <>
                <ReferenceLine
                    y={showMultiplier ? 1 : 100}
                    stroke="#888888"
                    strokeDasharray="3 3"
                    label={{
                    value: showMultiplier ? '1.00' : '100%',
                    position: 'right',
                    fontSize: 12,
                    fill: '#888888',
                    }}
                />
                {!showMultiplier && (
                    <ReferenceLine
                        y={90}
                        stroke="red"
                        strokeDasharray="3 3"
                        label={{
                        value: '90%',
                        position: 'right',
                        fontSize: 12,
                        fill: 'red',
                        }}
                    />
                )}
            </>
          )}
          {/* Truth line - solid blue */}
          <Line
            type="monotone"
            dataKey={actualLabel}
            name={actualLabel}
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
          />
          {/* Compare line - solid with different color（日期对比时 name 为具体日期，与 chartData 键一致） */}
          {resolvedCompareData && resolvedCompareData.map((c, index) => (
            <Line
              key={`compare-${index}`}
              type="monotone"
              dataKey={c.name}
              name={c.name}
              stroke={MODEL_COLORS[(index + 1) % MODEL_COLORS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
          {/* Prediction lines - dashed with different colors (disabled in compare mode or non-daily mode) */}
          {!compareRoas &&
            isDaily &&
            Array.from(predictCumulativeData.keys()).map((modelKey, index) => (
              <Line
                key={modelKey}
                type="monotone"
                dataKey={modelKey}
                name={modelKey}
                stroke={MODEL_COLORS[index % MODEL_COLORS.length]}
                strokeDasharray="5 5"
                strokeWidth={2}
                dot={false}
              />
            ))}
          {/* Other dates lines - solid for real data */}
          {Array.from(otherDatesDataMap.keys()).map((date, index) => (
            <Line
              key={date}
              type="monotone"
              dataKey={date}
              name={otherDatesDataMap.get(date).displayLabel}
              stroke={
                MODEL_COLORS[index % MODEL_COLORS.length]
              }
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}

export default RoasAnalysisChart
